import React, { useMemo, useEffect, useRef, useState } from "react";
import * as Diff from "diff";

const VersionComparisonTab = ({ comparisonData }) => {
  const baseContentRef = useRef(null);
  const targetContentRef = useRef(null);

  // State to hold processed HTML
  const [processedBaseHtml, setProcessedBaseHtml] = useState("");
  const [processedTargetHtml, setProcessedTargetHtml] = useState("");

  // Flag to prevent infinite loop during sync
  const isSyncingScroll = useRef(false);

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  const baseHtml = useMemo(
    () => decodeHtml(comparisonData?.meta?.raw_base || ""),
    [comparisonData?.meta?.raw_base]
  );
  const targetHtml = useMemo(
    () => decodeHtml(comparisonData?.meta?.raw_target || ""),
    [comparisonData?.meta?.raw_target]
  );



  // Calculate stats
  const stats = useMemo(() => {
    let added = 0,
      removed = 0,
      modified = 0;

    if (baseHtml && targetHtml) {
      const stripHtml = (html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || "";
      };

      const baseText = stripHtml(baseHtml);
      const targetText = stripHtml(targetHtml);

      const diff = Diff.diffWords(baseText, targetText);

      diff.forEach((part) => {
        if (part.added) {
          added += part.value.split(/\s+/).filter((w) => w).length;
        } else if (part.removed) {
          removed += part.value.split(/\s+/).filter((w) => w).length;
        }
      });

      modified = diff.filter((p) => p.added || p.removed).length;
    }

    return {
      added,
      removed,
      total: comparisonData?.hunks?.length || modified,
    };
  }, [comparisonData?.hunks, baseHtml, targetHtml]);

  // Function to strip HTML and get plain text
  const getTextFromHtml = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };



  const highlightDifferences = () => {
    if (!baseHtml || !targetHtml) return;

    const baseText = getTextFromHtml(baseHtml);
    const targetText = getTextFromHtml(targetHtml);

    // ✅ Step 1: Get word-level diff with positions
    const diff = Diff.diffWords(baseText, targetText);

    // ✅ Step 2: Calculate exact positions of changed words
    let basePosition = 0;
    let targetPosition = 0;
    const changedPositions = { base: [], target: [] };

    diff.forEach((part) => {
      const wordCount = part.value.split(/\s+/).filter(w => w).length;

      if (part.removed) {
        // Store removed word positions in base
        changedPositions.base.push({
          startPos: basePosition,
          endPos: basePosition + wordCount,
          type: 'removed'
        });
        basePosition += wordCount;
      } else if (part.added) {
        // Store added word positions in target
        changedPositions.target.push({
          startPos: targetPosition,
          endPos: targetPosition + wordCount,
          type: 'added'
        });
        targetPosition += wordCount;
      } else {
        // Unchanged text - advance both pointers
        basePosition += wordCount;
        targetPosition += wordCount;
      }
    });

    // ✅ Step 3: Highlight only changed positions
    const wrapSpecificWords = (html, changedRanges, className) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      let wordIndex = 0;

      const walkTextNodes = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          const words = text.split(/(\s+)/);

          const fragment = document.createDocumentFragment();

          words.forEach(word => {
            // Check if current word is in any changed range
            const isChanged = changedRanges.some(range =>
              wordIndex >= range.startPos && wordIndex < range.endPos && word.trim()
            );

            if (isChanged && word.trim()) {
              const mark = document.createElement('mark');
              mark.className = className;
              mark.textContent = word;
              fragment.appendChild(mark);
            } else {
              fragment.appendChild(document.createTextNode(word));
            }

            // Increment word index only for actual words
            if (word.trim()) {
              wordIndex++;
            }
          });

          node.parentNode.replaceChild(fragment, node);
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'MARK') {
          Array.from(node.childNodes).forEach(walkTextNodes);
        }
      };

      walkTextNodes(doc.body);
      return doc.body.innerHTML;
    };

    // ✅ Step 4: Apply position-based highlighting
    const highlightedBase = wrapSpecificWords(baseHtml, changedPositions.base, 'diff-removed');
    const highlightedTarget = wrapSpecificWords(targetHtml, changedPositions.target, 'diff-added');

    setProcessedBaseHtml(highlightedBase);
    setProcessedTargetHtml(highlightedTarget);
  };

  // ✅ SYNCHRONIZED SCROLL HANDLERS
  const handleBaseScroll = (e) => {
    if (isSyncingScroll.current) return;

    isSyncingScroll.current = true;

    if (targetContentRef.current) {
      const scrollPercentage =
        e.target.scrollTop / (e.target.scrollHeight - e.target.clientHeight);

      const targetScrollTop =
        scrollPercentage * (targetContentRef.current.scrollHeight - targetContentRef.current.clientHeight);

      targetContentRef.current.scrollTop = targetScrollTop;
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      isSyncingScroll.current = false;
    }, 50);
  };

  const handleTargetScroll = (e) => {
    if (isSyncingScroll.current) return;

    isSyncingScroll.current = true;

    if (baseContentRef.current) {
      const scrollPercentage =
        e.target.scrollTop / (e.target.scrollHeight - e.target.clientHeight);

      const baseScrollTop =
        scrollPercentage * (baseContentRef.current.scrollHeight - baseContentRef.current.clientHeight);

      baseContentRef.current.scrollTop = baseScrollTop;
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      isSyncingScroll.current = false;
    }, 50);
  };

  // Apply highlighting when HTML changes
  useEffect(() => {
    if (baseHtml && targetHtml) {
      const timer = setTimeout(() => {
        highlightDifferences();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setProcessedBaseHtml(baseHtml);
      setProcessedTargetHtml(targetHtml);
    }
  }, [baseHtml, targetHtml]);

  // Early return after all hooks
  if (!comparisonData) {
    return (
      <div className="versions-empty">
        <i className="bi bi-diagram-3"></i>
        <p>No comparison data available</p>
      </div>
    );
  }

  const { meta } = comparisonData;

  return (
    <div className="version-comparison-container">
      {/* SIDE BY SIDE COMPARISON */}
      <div className="comparison-body">
        {/* LEFT - BASE VERSION (OLD) */}
        <div className="comparison-panel base-panel">
          <div className="panel-header">
            <div className="panel-header-left">
              <i className="bi bi-file-earmark-text"></i>
              <h6>Version {meta.base_version}</h6>
            </div>
            <span className="version-tag old">
              <i className="bi bi-clock-history"></i>
              v{meta.base_version}
            </span>
          </div>
          <div
            className="panel-content"
            ref={baseContentRef}
            onScroll={handleBaseScroll}
          >
            <div
              className="html-preview base-html"
              dangerouslySetInnerHTML={{
                __html: processedBaseHtml || "<p class='no-content'>No content available</p>",
              }}
            />
          </div>
        </div>

        {/* RIGHT - TARGET VERSION (NEW) */}
        <div className="comparison-panel target-panel">
          <div className="panel-header">
            <div className="panel-header-left">
              <i className="bi bi-file-earmark-check"></i>
              <h6>Version {meta.target_version}</h6>
            </div>
            <span className="version-tag new">
              <i className="bi bi-check-circle"></i>
              v{meta.target_version}
            </span>
          </div>
          <div
            className="panel-content"
            ref={targetContentRef}
            onScroll={handleTargetScroll}
          >
            <div
              className="html-preview target-html"
              dangerouslySetInnerHTML={{
                __html: processedTargetHtml || "<p class='no-content'>No content available</p>",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionComparisonTab;
